import { getOrderPackageListAsync } from 'src/redux/actions/orderPackageActions'
import { getOmsShipmentListAsync } from 'src/redux/actions/omsShippingActions'

/**
 * Shipping route types:
 * - 'packages': Order-packages exist → use Manage Packages route
 * - 'direct':   Direct shipments exist (no order-packages) → use Manage Shipments route
 * - 'open':     Neither exists yet → both routes available
 */
export type ShippingRoute = 'packages' | 'direct' | 'open'

/**
 * Determines which shipping route an order is using.
 *
 * Rules:
 *  1. If order-packages ≥ 1 → route = 'packages'
 *  2. If order-packages = 0 AND shipments ≥ 1 → route = 'direct'
 *  3. If order-packages = 0 AND shipments = 0 → route = 'open'
 *
 * @param orderId  The order to check
 * @param dispatch Redux dispatch function
 * @returns The determined ShippingRoute
 */
export async function determineShippingRoute(
  orderId: number,
  dispatch: (action: any) => any
): Promise<ShippingRoute> {
  try {
    // Step 1: Check for order-packages
    const pkgRes = await dispatch(
      getOrderPackageListAsync({ order_id: orderId, page: 1, limit: 1 })
    ).unwrap()
    const pkgList = pkgRes?.results?.data || pkgRes?.data || []
    const hasPackages = Array.isArray(pkgList) && pkgList.length > 0

    if (hasPackages) return 'packages'

    // Step 2: No packages — check for direct shipments
    const shipRes = await dispatch(
      getOmsShipmentListAsync({ order_id: String(orderId), page: 1, limit: 1 })
    ).unwrap()
    const shipList = shipRes?.results?.data || shipRes?.data || shipRes?.results || []
    const hasShipments = Array.isArray(shipList) && shipList.length > 0

    if (hasShipments) return 'direct'

    // Step 3: Neither exists — both routes available
    return 'open'
  } catch {
    // On error, default to 'open' so we don't accidentally block the user
    return 'open'
  }
}

/**
 * Helper to determine if an order has any pending quantity left to pack/ship.
 * @param orderLinesList The list of order line items from the API
 * @returns boolean True if there is at least 1 item with pending quantity > 0
 */
export function hasPendingQty(orderLinesList: any[]): boolean {
  if (!Array.isArray(orderLinesList) || orderLinesList.length === 0) return false
  return orderLinesList.some((item) => (item.pending_qty || 0) > 0)
}
